import React from "react";

import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";

const MAX_SUPPORT_LIMIT_ROWS = 3;

const emptySupportLimit = () => ({
	maxGrants: "",
	windowDays: "",
});

const normalizeLimits = (limits) => {
	if (!Array.isArray(limits)) return [];

	return limits.slice(0, MAX_SUPPORT_LIMIT_ROWS).map((limit) => ({
		maxGrants: limit?.maxGrants === null || limit?.maxGrants === undefined ? "" : String(limit.maxGrants),
		windowDays: limit?.windowDays === null || limit?.windowDays === undefined ? "" : String(limit.windowDays),
	}));
};

class VipFreespinsSettings extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			limits: [],
			loading: true,
			saving: false,
			error: "",
			fieldErrors: {},
		};
	}

	componentDidMount() {
		if (this.props.visible) {
			this.loadSettings();
		}
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.visible && this.props.visible) {
			this.loadSettings();
		}
	}

	loadSettings = () => {
		this.setState({ loading: true, error: "", fieldErrors: {} });

		this.props.Interface.loadFreespinsSettings()
			.then((settings) => {
				this.setState({
					limits: normalizeLimits(settings?.supportAllReasonsGrantLimits),
					loading: false,
					error: "",
					fieldErrors: {},
				});
			})
			.catch((error) => {
				this.setState({
					loading: false,
					error: error?.text || error?.message || String(error),
				});
			});
	};

	updateLimitField = (index, field, value) => {
		this.setState((state) => {
			const errorKey = `${index}.${field}`;

			return {
				limits: state.limits.map((limit, limitIndex) => (
					limitIndex === index ? { ...limit, [field]: value } : limit
				)),
				fieldErrors: {
					...state.fieldErrors,
					[errorKey]: "",
					limits: "",
				},
				error: "",
			};
		});
	};

	addLimit = () => {
		this.setState((state) => {
			if (state.limits.length >= MAX_SUPPORT_LIMIT_ROWS) {
				return {
					fieldErrors: {
						...state.fieldErrors,
						limits: `Maximum ${MAX_SUPPORT_LIMIT_ROWS} limits are allowed`,
					},
				};
			}

			return {
				limits: [
					...state.limits,
					emptySupportLimit(),
				],
				fieldErrors: {
					...state.fieldErrors,
					limits: "",
				},
			};
		});
	};

	removeLimit = (index) => {
		this.setState((state) => ({
			limits: state.limits.filter((limit, limitIndex) => limitIndex !== index),
			fieldErrors: {
				...state.fieldErrors,
				limits: "",
			},
			error: "",
		}));
	};

	validateLimits = () => {
		const errors = {};

		if (this.state.limits.length > MAX_SUPPORT_LIMIT_ROWS) {
			errors.limits = `Maximum ${MAX_SUPPORT_LIMIT_ROWS} limits are allowed`;
		}

		this.state.limits.forEach((limit, index) => {
			["maxGrants", "windowDays"].forEach((field) => {
				const value = String(limit[field] || "").trim();
				const errorKey = `${index}.${field}`;

				if (!value) {
					errors[errorKey] = "Required";
					return;
				}

				const numberValue = Number(value);
				if (!Number.isInteger(numberValue) || numberValue <= 0) {
					errors[errorKey] = "Enter a whole number greater than 0";
				}
			});
		});

		return errors;
	};

	saveSettings = () => {
		const fieldErrors = this.validateLimits();

		if (Object.keys(fieldErrors).length) {
			this.setState({
				fieldErrors,
				error: "Please fix the highlighted fields before saving.",
			});
			return;
		}

		this.setState({ saving: true, error: "", fieldErrors: {} });

		this.props.Interface.updateFreespinsSettings({
			supportAllReasonsGrantLimits: this.state.limits.map((limit) => ({
				maxGrants: Number(String(limit.maxGrants).trim()),
				windowDays: Number(String(limit.windowDays).trim()),
			})),
		})
			.then((settings) => {
				this.setState({
					limits: normalizeLimits(settings?.supportAllReasonsGrantLimits),
					saving: false,
					error: "",
					fieldErrors: {},
				});
				this.props.Interface.addNoty("Success", "VIP FS settings updated successfully");
			})
			.catch((error) => {
				this.setState({
					saving: false,
					error: error?.text || error?.message || String(error),
				});
			});
	};

	render() {
		const { limits, loading, saving, error, fieldErrors } = this.state;

		return (
			<Modal
				visible={this.props.visible}
				onDismiss={this.props.onDismiss}
				header="VIP FS Limits"
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={this.props.onDismiss} disabled={saving}>
								Cancel
							</Button>
							<Button variant="primary" onClick={this.saveSettings} loading={saving} disabled={loading || saving}>
								Save settings
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween direction="vertical" size="m">
					{error ? (
						<Alert type="error" header="Unable to save limits">
							{error}
						</Alert>
					) : null}
					{limits.length ? (
						<SpaceBetween direction="vertical" size="s">
							{limits.map((limit, index) => (
								<div
									key={`global-support-limit-${index}`}
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr auto",
										columnGap: "24px",
										alignItems: "end",
									}}
								>
									<FormField label="Max successful grants" errorText={fieldErrors[`${index}.maxGrants`]} stretch>
										<Input
											type="number"
											inputMode="numeric"
											value={limit.maxGrants}
											onChange={({ detail }) => this.updateLimitField(index, "maxGrants", detail.value)}
											disabled={loading || saving}
											placeholder="5"
										/>
									</FormField>
									<FormField label="Window, days" errorText={fieldErrors[`${index}.windowDays`]} stretch>
										<Input
											type="number"
											inputMode="numeric"
											value={limit.windowDays}
											onChange={({ detail }) => this.updateLimitField(index, "windowDays", detail.value)}
											disabled={loading || saving}
											placeholder="1"
										/>
									</FormField>
									<Button onClick={() => this.removeLimit(index)} disabled={loading || saving}>
										Remove
									</Button>
								</div>
							))}
						</SpaceBetween>
					) : (
						<Box variant="p">
							No limits are configured right now. Support accounts can grant VIP FreeSpins without an all-reasons limit.
						</Box>
					)}
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={this.addLimit} disabled={loading || saving || limits.length >= MAX_SUPPORT_LIMIT_ROWS}>
							Add limit
						</Button>
						{fieldErrors.limits ? (
							<Box color="text-status-error">{fieldErrors.limits}</Box>
						) : null}
					</SpaceBetween>
				</SpaceBetween>
			</Modal>
		);
	}
}

export default VipFreespinsSettings;
